import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu as MenuIcon, Phone, X, Instagram, Globe } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { PHONE_TEL, PHONE_DISPLAY, INSTAGRAM_URL } from "@/site";

const LINKS = [
  { to: "/", key: "home", id: "home" },
  { to: "/chi-siamo", key: "about", id: "about" },
  { to: "/menu", key: "menu", id: "menu" },
  { to: "/contatti", key: "contact", id: "contact" },
];

export default function Header() {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-ink/90 backdrop-blur-xl border-b border-gold/15 py-2.5"
            : "bg-transparent py-5"
        }`}
        data-testid="site-header"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-10">
          <Link to="/" data-testid="header-logo-link" aria-label="Gunè — Home">
            <img
              src="/img/logo.png"
              alt="Gunè"
              className="h-11 w-auto sm:h-12"
            />
          </Link>

          <nav className="hidden items-center gap-9 lg:flex">
            {LINKS.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                data-testid={`nav-${l.id}`}
                className={({ isActive }) =>
                  `link-gold text-[13px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                    isActive ? "text-gold" : "text-white/80 hover:text-white"
                  }`
                }
              >
                {t.nav[l.key]}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3 sm:gap-5">
            <div
              className="flex items-center font-mono text-xs tracking-widest"
              data-testid="language-switch"
            >
              {["it", "en"].map((code, i) => (
                <span key={code} className="flex items-center">
                  {i === 1 && <span className="mx-1.5 text-gold/40">|</span>}
                  <button
                    onClick={() => setLang(code)}
                    data-testid={`lang-switch-${code}`}
                    className={`uppercase transition-colors duration-300 ${
                      lang === code ? "text-gold" : "text-white/50 hover:text-white"
                    }`}
                  >
                    {code}
                  </button>
                </span>
              ))}
            </div>

            <a
              href={PHONE_TEL}
              data-testid="header-phone-cta"
              className="hidden items-center gap-2.5 border border-gold/50 px-5 py-2.5 text-[12px] uppercase tracking-[0.18em] text-gold transition-all duration-500 hover:bg-gold hover:text-black sm:inline-flex"
            >
              <Phone size={14} />
              {PHONE_DISPLAY}
            </a>

            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-open-btn"
              aria-label="Apri menu"
              className="text-white transition-colors hover:text-gold lg:hidden"
            >
              <MenuIcon size={26} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[60] flex flex-col bg-ink/[0.98] backdrop-blur-2xl"
            data-testid="mobile-menu-overlay"
          >
            <div className="flex items-center justify-between px-5 py-5 sm:px-10">
              <img src="/img/logo.png" alt="Gunè" className="h-11 w-auto" />
              <button
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close-btn"
                aria-label="Chiudi menu"
                className="text-white transition-colors hover:text-gold"
              >
                <X size={28} />
              </button>
            </div>

            <nav className="flex flex-1 flex-col justify-center gap-2 px-8 sm:px-12">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.id}`}
                    className={({ isActive }) =>
                      `font-display italic text-5xl leading-[1.15] sm:text-6xl ${
                        isActive ? "text-gold" : "text-white hover:text-gold transition-colors"
                      }`
                    }
                  >
                    {t.nav[l.key]}
                  </NavLink>
                </motion.div>
              ))}
            </nav>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="flex items-center justify-between px-8 pb-10 sm:px-12"
            >
              <a
                href={PHONE_TEL}
                data-testid="mobile-phone-cta"
                className="inline-flex items-center gap-3 bg-gold px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] text-black"
              >
                <Phone size={15} />
                {PHONE_DISPLAY}
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-instagram-link"
                aria-label="Instagram"
                className="text-white/70 transition-colors hover:text-gold"
              >
                <Instagram size={22} />
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
